/* ═══════════════════════════════════════════════════════════════════════
 * [임시:NATIVE-MAP-PREVIEW]  네이티브 지도 화면 확인용 — /ds/native-maps
 *
 * 네이티브 지도 5화면은 앱 안에서만 뜨고, 앱에는 URL 입력창이 없어서
 * 재디자인 결과를 눌러 볼 방법이 없습니다. 그래서 기존 브릿지를 그대로
 * 불러 실제 화면을 띄우는 버튼만 모아 둡니다 — 목업이 아닙니다.
 *
 * ⚠️ 확인이 끝나면 지웁니다. 지울 것 세 곳, 모두 같은 표식이 붙어 있습니다.
 *      1) 이 파일
 *      2) routers/index.tsx 의 import + "/ds/native-maps" 라우트
 *      3) pages/home/index.tsx 의 링크 배너
 *    `grep -rn "\[임시:NATIVE-MAP-PREVIEW\]" src` 로 전부 찾을 수 있습니다.
 *
 * 데모 문구라 i18n 을 타지 않습니다 (다른 /ds/* 미리보기와 같은 규칙).
 * ═══════════════════════════════════════════════════════════════════════ */

import { useState } from 'react';
import { AppScreen, Section, SectionHeader, Button, Divider } from 'components/ds';
import {
    isNativeApp,
    openNativePlaceExplore,
    openNativeGooglePlaceSearch,
    openNativePlaceSearch,
    openNativeMapView,
    openNativeRealtimeLocation,
} from 'common/webview';
import config from 'config/config';

/** 파리 — 해외 좌표여야 네이티브 위임 조건을 만족합니다 */
const PARIS = { lat: 48.8584, lng: 2.2945 };

const SAMPLE_PLACES = [
    {
        id: 'demo-1', name: 'Le Café Marly', address: '93 Rue de Rivoli, 75001 Paris',
        lat: 48.8622, lng: 2.3355, rating: 4.1, categoryLabel: '카페',
    },
    {
        id: 'demo-2', name: 'Musée du Louvre', address: 'Rue de Rivoli, 75001 Paris',
        lat: 48.8606, lng: 2.3376, rating: 4.7, categoryLabel: '관광',
    },
    {
        id: 'demo-3', name: 'Tour Eiffel', address: 'Champ de Mars, 75007 Paris',
        lat: 48.8584, lng: 2.2945, rating: 4.6, categoryLabel: '관광',
    },
    {
        id: 'demo-4', name: 'Bouillon Chartier', address: '7 Rue du Faubourg Montmartre, 75009 Paris',
        lat: 48.8719, lng: 2.3430, rating: 4.0, categoryLabel: '음식점',
    },
    {
        id: 'demo-5', name: 'Hôtel Plaza Athénée', address: '25 Av. Montaigne, 75008 Paris',
        lat: 48.8663, lng: 2.3040, rating: 4.8, categoryLabel: '숙소',
    },
];

export const NativeMapPreview = () => {
    const [result, setResult] = useState<string>('');
    const native = isNativeApp();

    const show = (label: string, v: unknown) =>
        setResult(`${label} → ${v ? JSON.stringify(v, null, 1) : '취소'}`);

    const explore = async (mode: 'general' | 'attraction' | 'hotel') => {
        const r = await openNativePlaceExplore({
            places: SAMPLE_PLACES,
            center: PARIS,
            confirmButtonText: '이 장소로 정하기',
            apiUrl: config.API_URL,
            mode,
        });
        show(`장소 탐색 · ${mode}`, r);
    };

    return (
        <AppScreen title="네이티브 지도 확인" bar="sub">
            <Section>
                <SectionHeader
                    title="네이티브 지도 5화면"
                    sub={native
                        ? '버튼을 누르면 앱의 네이티브 지도가 그대로 뜹니다. 파리 좌표를 씁니다.'
                        : '브라우저에서는 아무 일도 일어나지 않습니다 — 앱에서 열어야 합니다.'}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Button size="lg" onClick={() => explore('general')}>
                        장소 탐색 — 일반 (PlaceExploreModal)
                    </Button>
                    <Button size="lg" variant="secondary" onClick={() => explore('attraction')}>
                        장소 탐색 — 관광 (평점 알약 마커)
                    </Button>
                    <Button size="lg" variant="secondary" onClick={() => explore('hotel')}>
                        장소 탐색 — 숙박 (가격 라벨 마커)
                    </Button>
                </div>
            </Section>

            <Divider />

            <Section>
                <SectionHeader title="나머지 네 화면" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Button size="lg" variant="secondary" onClick={async () => {
                        const r = await openNativeGooglePlaceSearch({
                            initialQuery: 'Louvre',
                            initialLocation: PARIS,
                            confirmButtonText: '이 장소로 정하기',
                            placeholder: '장소를 검색하세요',
                        });
                        show('구글 장소 검색', r);
                    }}>
                        구글 장소 검색 (GooglePlacePickerModal)
                    </Button>

                    <Button size="lg" variant="secondary" onClick={async () => {
                        const r = await openNativePlaceSearch({
                            currentLocation: PARIS,
                            initialQuery: 'Eiffel',
                        });
                        show('장소 고르기', r);
                    }}>
                        장소 고르기 (PlacePickerModal)
                    </Button>

                    <Button size="lg" variant="secondary" onClick={() => {
                        openNativeMapView({
                            name: 'Tour Eiffel',
                            address: 'Champ de Mars, 75007 Paris',
                            lat: PARIS.lat,
                            lng: PARIS.lng,
                        });
                    }}>
                        지도 보기 (MapViewModal)
                    </Button>

                    <Button size="lg" variant="secondary" onClick={() => {
                        openNativeRealtimeLocation({
                            meetingId: 'preview',
                            destination: {
                                name: 'Tour Eiffel',
                                address: 'Champ de Mars, 75007 Paris',
                                lat: PARIS.lat,
                                lng: PARIS.lng,
                            },
                        });
                    }}>
                        실시간 위치 (RealtimeLocationScreen)
                    </Button>
                </div>
                <p style={{ marginTop: 12, fontSize: 12, color: 'var(--t-ink-3)', wordBreak: 'keep-all' }}>
                    실시간 위치는 존재하지 않는 모임(preview)이라 참여자 목록이 비어 있습니다.
                    지도·상단바·탭·시트 모양만 확인하는 용도입니다.
                </p>
            </Section>

            {result ? (
                <>
                    <Divider />
                    <Section>
                        <SectionHeader title="돌려받은 값" />
                        <pre style={{
                            margin: 0, padding: 12, background: 'var(--t-surface-sunken)',
                            fontSize: 11, lineHeight: 1.5, color: 'var(--t-ink-2)',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        }}>{result}</pre>
                    </Section>
                </>
            ) : null}
        </AppScreen>
    );
};
